import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle2, XCircle, AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import withdrawalService from '../../services/withdrawalService';

const WithdrawalHistory = ({ projectId, userId, showProjectInfo = false }) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadWithdrawals();
  }, [projectId, userId, pagination.page]);

  const loadWithdrawals = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (projectId) {
        response = await withdrawalService.getProjectWithdrawals(projectId, {
          page: pagination.page,
          limit: pagination.limit
        });
      } else if (userId) {
        response = await withdrawalService.getUserWithdrawals(userId, {
          page: pagination.page,
          limit: pagination.limit
        });
      } else {
        throw new Error('Either projectId or userId must be provided');
      }

      if (response.success) {
        setWithdrawals(response.data.withdrawals);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (err) {
      console.error('Failed to load withdrawals:', err);
      setError(err.message || 'Failed to load withdrawal history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      pending: <Clock className="w-4 h-4 text-yellow-600" />,
      approved: <CheckCircle2 className="w-4 h-4 text-blue-600" />,
      processing: <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />,
      submitted: <CheckCircle2 className="w-4 h-4 text-blue-600" />,
      completed: <CheckCircle2 className="w-4 h-4 text-green-600" />,
      rejected: <XCircle className="w-4 h-4 text-red-600" />,
      failed: <AlertCircle className="w-4 h-4 text-red-600" />
    };
    return iconMap[status] || <Clock className="w-4 h-4 text-gray-600" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  if (loading && withdrawals.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        <span className="ml-2 text-gray-600">Loading withdrawal history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadWithdrawals}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (withdrawals.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Withdrawals Yet</h3>
        <p className="text-gray-600">
          {projectId
            ? "This project hasn't made any withdrawal requests yet."
            : "You haven't made any withdrawal requests yet."
          }
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Withdrawals List */}
      <div className="space-y-3">
        {withdrawals.map((withdrawal) => (
          <div
            key={withdrawal.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Project Info (if showing multiple projects) */}
                {showProjectInfo && withdrawal.project && (
                  <div className="mb-2">
                    <h4 className="font-medium text-gray-900">{withdrawal.project.title}</h4>
                    <p className="text-sm text-gray-500">{withdrawal.project.category}</p>
                  </div>
                )}

                {/* Amount and Status */}
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(withdrawal.status)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${withdrawalService.getStatusBadgeClass(withdrawal.status)}`}>
                      {withdrawalService.formatStatus(withdrawal.status, withdrawal.currency).label}
                    </span>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {withdrawalService.formatAmount(
                      withdrawal.approved_amount || withdrawal.requested_amount,
                      withdrawal.currency
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Requested:</span> {formatDate(withdrawal.requested_at)}
                  </div>
                  {withdrawal.approved_at && (
                    <div>
                      <span className="font-medium">Approved:</span> {formatDate(withdrawal.approved_at)}
                    </div>
                  )}
                  {withdrawal.completed_at && (
                    <div>
                      <span className="font-medium">Completed:</span> {formatDate(withdrawal.completed_at)}
                    </div>
                  )}
                  {withdrawal.currency === 'NGN' ? (
                    <div>
                      <span className="font-medium">Bank:</span> {withdrawal.bank_name} ({withdrawal.account_number})
                    </div>
                  ) : (
                    <div>
                      <span className="font-medium">Wallet:</span>
                      <span className="font-mono text-xs ml-1">
                        {withdrawal.recipient_wallet?.slice(0, 8)}...{withdrawal.recipient_wallet?.slice(-6)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Auto-approval indicator (Only for Pi) */}
                {withdrawal.auto_approved && withdrawal.currency === 'PI' && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Auto-approved
                    </span>
                  </div>
                )}

                {/* Rejection reason */}
                {withdrawal.status === 'rejected' && withdrawal.rejection_reason && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                    <p className="text-sm text-red-800">
                      <span className="font-medium">Rejection reason:</span> {withdrawal.rejection_reason}
                    </p>
                  </div>
                )}

                {/* Request reason */}
                {withdrawal.request_reason && (
                  <div className="mt-2 p-2 bg-gray-50 rounded">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Reason:</span> {withdrawal.request_reason}
                    </p>
                  </div>
                )}
              </div>

              {/* Transaction Link */}
              {withdrawal.transaction_hash && (
                <div className="ml-4 flex flex-col items-end">
                  {withdrawal.currency === 'PI' ? (
                    <a
                      href={`https://blockexplorer.minepi.com/mainnet/transactions/${withdrawal.transaction_hash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Transaction
                    </a>
                  ) : (
                    <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded">
                      Ref: {withdrawal.transaction_hash.slice(0, 12)}...
                    </span>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    {withdrawal.tx_hash_short}
                  </div>
                </div>
              )}
            </div>

            {/* Processing Time Estimate */}
            {withdrawalService.isWithdrawalInProgress(withdrawal.status) && (
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Estimated processing time:</span>{' '}
                  {withdrawalService.getEstimatedProcessingTime(
                    withdrawal.status,
                    withdrawal.approved_amount || withdrawal.requested_amount
                  )}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} withdrawals
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-gray-600">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalHistory;
